import * as THREE from 'three'
import { Results as HandResults, NormalizedLandmark } from '@mediapipe/hands'
import { Landmark } from './pose'

// NOTE: this was converted from blender where Z is up
// so the naming conventions could be mixed; but it works in three.js!

type JointQuaternions = [THREE.Quaternion, THREE.Quaternion]

/**
 * Calculates the normal vector of a triangle formed by three joints.
 * @param verts Array of three vertices
 * @returns {Vector} Normal vector of the triangle
 */
export function triNormal (verts: THREE.Vector3[]): THREE.Vector3 {
  const edgeBA = verts[1].clone().sub(verts[0])
  const edgeCB = verts[2].clone().sub(verts[1])

  // TODO: normalize?
  return edgeBA.cross(edgeCB)//.normalize()
}

/**
 * Creates a rotation matrix from up and forward vectors
 * @param up Up vector (z-axis)
 * @param forward Forward vector (y-axis)
 * @returns Rotation matrix
 */
export function toTrackMat (up: THREE.Vector3, forward: THREE.Vector3) {
  const z_axis = up.clone().normalize()
  const y_axis = forward.clone().normalize()
  const x_axis = y_axis.clone().cross(z_axis).normalize()

  const mat = new THREE.Matrix4(
    x_axis.x, y_axis.x, z_axis.x, 0,
    x_axis.y, y_axis.y, z_axis.y, 0,
    x_axis.z, y_axis.z, z_axis.z, 0,
    0,        0,        0,        1,
  )

  return mat
}

export function toTrackQuat (up: THREE.Vector3, forward: THREE.Vector3) {
  return new THREE.Quaternion().setFromRotationMatrix(toTrackMat(up, forward))
}

/**
 * Creates a quaternion rotation from up and forward vectors
 * Note: Three.js quaternions are stored as (x,y,z,w) internally
 */
export function lookRotation (up: THREE.Vector3, forward: THREE.Vector3): THREE.Quaternion {
  const mat = toTrackMat(up, forward)
  const quat = new THREE.Quaternion().setFromRotationMatrix(mat)

  return quat
}

/**
 * Calculates two quaternions of a joint, the incoming bone and the outgoing bone
 *
 * NOTE: it assumes the joint is forming a triangle in (idx, idx + 2, idx + 4)
 */
export function calcJointQuats (
  pose: number[][],
  idx: number,
  invertUp = false,
  rollAngle = 0,
): JointQuaternions {
  const verts = [
    new THREE.Vector3(...pose[idx]),
    new THREE.Vector3(...pose[idx + 2]),
    new THREE.Vector3(...pose[idx + 4]),
  ]

  const up = triNormal(verts)
  if (invertUp)
    up.negate()

  const forwardIn = new THREE.Vector3().subVectors(
    verts[0],
    verts[1],
  )

  const forwardOut = new THREE.Vector3().subVectors(
    verts[1],
    verts[2],
  )

  let quatIn = lookRotation(up, forwardIn)
  let quatOut = lookRotation(up, forwardOut)

  if (rollAngle) {
    const radians = rollAngle * Math.PI / 180
    const quatForwardIn = new THREE.Quaternion().setFromAxisAngle(forwardIn.normalize(), radians)
    const quatForwardOut = new THREE.Quaternion().setFromAxisAngle(forwardOut.normalize(), radians)

    quatIn = quatForwardIn.multiply(quatIn)
    quatOut = quatForwardOut.multiply(quatOut)
  }

  return [quatIn, quatOut]
}

export function armQuats (pose: number[][], side: number): JointQuaternions {
  return calcJointQuats(pose, Landmark.LEFT_SHOULDER + side, side == 0)
}

export function handQuat (pose: number[][], side: number): JointQuaternions {
  return calcJointQuats(pose, Landmark.LEFT_WRIST + side, side == 0)
}

export function legQuats (pose: number[][], side: number): JointQuaternions {
  return calcJointQuats(pose, Landmark.LEFT_HIP + side, false, 90)
}

export function footQuat (pose: number[][], side: number): JointQuaternions {
  return calcJointQuats(pose, Landmark.LEFT_ANKLE + side, true, 90)
}

export function hipQuat (pose: number[][]): THREE.Quaternion {
  // Get vertices for triangle
  const verts = [
    new THREE.Vector3(...pose[Landmark.RIGHT_SHOULDER]),
    new THREE.Vector3(...pose[Landmark.LEFT_SHOULDER]),
    new THREE.Vector3(...pose[Landmark.LEFT_HIP]),
  ]

  // Get center points of hips and shoulders
  // NOTE: in blazepose this is simply 0,0,0
  const hips = new THREE.Vector3()
  const neck = verts[0].clone().add(verts[1]).divideScalar(2).negate()
  const shoulders = verts[1].clone().sub(verts[0])

  // this is the vector forward from the body, but is up relative to body bone
  const yAxis = shoulders.cross(neck)

  // likewise the body up vector
  const zAxis = neck.sub(hips)

  return toTrackQuat(yAxis, zAxis)
}

export function handQuat2 (hand: NormalizedLandmark[], invert = false): THREE.Quaternion {
  const verts = [
    new THREE.Vector3(hand[0].x, hand[0].y, hand[0].z),
    new THREE.Vector3(hand[5].x, hand[5].y, hand[5].z),
    new THREE.Vector3(hand[9].x, hand[9].y, hand[9].z),
  ]
  const up = triNormal(verts)
  if (invert)
    up.negate()

  const forward = new THREE.Vector3().subVectors(
    verts[0],
    verts[2],
  )

  return toTrackQuat(up, forward)
}

export function calcBoneRotations (pose: number[][], hands?: HandResults): Record<number, THREE.Quaternion> {
  // IMPORTANT NOTE: I mirrored all the angles, but in an annoying way
  // simply by mapping RightAngle: Angle of Left Joint
  // in the future it would be better to simply return a mapping RightAngle: Angle of Right Joint
  // and mirror after the fact if needed...
  // SO BE MINDFUL OF THIS!

  // arms
  const [quatUpperLeft, quatLowerLeft] = armQuats(pose, 0)
  const [quatUpperRight, quatLowerRight] = armQuats(pose, 1)

  // hands
  const handResults = {} as Record<number, THREE.Quaternion>
  let quatHandLeft = null
  let quatHandRight = null
  if (hands) {
    // process hands and swap sides if necessary
    if (hands.multiHandWorldLandmarks.length > 1)
      quatHandLeft = handQuat2(hands.multiHandWorldLandmarks[1], hands.multiHandedness[1].label == 'Left')

    if (hands.multiHandWorldLandmarks.length > 0) {
      quatHandRight = handQuat2(hands.multiHandWorldLandmarks[0], hands.multiHandedness[0].label == 'Left')
      if (hands.multiHandedness[0].label != 'Left') {
        const tmp = quatHandLeft
        quatHandLeft = quatHandRight
        quatHandRight = tmp
      }
    }

    // Apply additional rotations to hands
    // const handRotAxis = new THREE.Vector3(1, 0, 0).normalize()
    // if (quatHandLeft)
    //   quatHandLeft.multiply(new THREE.Quaternion().setFromAxisAngle(handRotAxis, Math.PI / -8))
    // if (quatHandRight)
    //   quatHandRight.multiply(new THREE.Quaternion().setFromAxisAngle(handRotAxis, Math.PI / -8))
  } else {
    quatHandLeft = handQuat(pose, 0)[0]
    quatHandRight = handQuat(pose, 1)[0]

    // Apply additional rotations to hands
    const handRotAxis = new THREE.Vector3(1, -2, 0).normalize()
    quatHandLeft.multiply(new THREE.Quaternion().setFromAxisAngle(handRotAxis, Math.PI / -8))
    quatHandRight.multiply(new THREE.Quaternion().setFromAxisAngle(handRotAxis, Math.PI / -8))
  }

  if (quatHandLeft)
    handResults[Landmark.RIGHT_WRIST] = quatHandLeft

  if (quatHandRight)
    handResults[Landmark.LEFT_WRIST] = quatHandRight

  // legs
  const [quatLegUpperLeft, quatLegLowerLeft] = legQuats(pose, 0)
  const [quatLegUpperRight, quatLegLowerRight] = legQuats(pose, 1)

  // feet
  // NOTE: this returns rotation of the heel-foot, not the ankle-heel
  // hence the dictionary key is Landmark.x_HEEL which is an exception to the above
  const [quatFootLeft] = footQuat(pose, 0)
  const [quatFootRight] = footQuat(pose, 1)

  // Apply additional rotations to legs
  const legRotAxis = new THREE.Vector3(0, 1, 0).normalize()
  quatLegUpperLeft.multiply(new THREE.Quaternion().setFromAxisAngle(legRotAxis, Math.PI / 8))
  quatLegUpperRight.multiply(new THREE.Quaternion().setFromAxisAngle(legRotAxis,Math.PI / -8))

  // Apply additional rotations to feet
  const footRotAxis = new THREE.Vector3(-1, 0, 0).normalize()
  quatFootLeft.multiply(new THREE.Quaternion().setFromAxisAngle(footRotAxis, Math.PI / -3))
  quatFootRight.multiply(new THREE.Quaternion().setFromAxisAngle(footRotAxis, Math.PI / -3))


  // NOTE: the landmarks are mirrored! need to convey this more explicitly
  return {
    [Landmark.RIGHT_SHOULDER]: quatUpperLeft,
    [Landmark.RIGHT_ELBOW]: quatLowerLeft,
    [Landmark.RIGHT_HIP]: quatLegUpperLeft,
    [Landmark.RIGHT_KNEE]: quatLegLowerLeft,
    [Landmark.RIGHT_HEEL]: quatFootLeft,

    [Landmark.LEFT_SHOULDER]: quatUpperRight,
    [Landmark.LEFT_ELBOW]: quatLowerRight,
    [Landmark.LEFT_HIP]: quatLegUpperRight,
    [Landmark.LEFT_KNEE]: quatLegLowerRight,
    [Landmark.LEFT_HEEL]: quatFootRight,

    ...handResults,
  }
}

/**
 * Sets the world rotation of a bone
 * @param bone The bone to set the rotation of
 * @param quat The quaternion to set the rotation to
 */
export function setBoneWorldRotation (bone: THREE.Bone, quat: THREE.Quaternion) {
  // TODO: not sure if there is a way to disable inheriting parent rotations
  // so here I cancel the parent's rotation by getting its world rotation

  // const parentWorldQuat = bone.bone.parent!.getWorldQuaternion(new THREE.Quaternion())
  // const localQuat = parentWorldQuat.clone().invert().multiply(quat)
  // bone.bone.quaternion.copy(localQuat)

  // this is just a more efficient version of the above
  bone.parent!.getWorldQuaternion(bone.quaternion).invert().multiply(quat)
}

const ZERO = new THREE.Vector3(0, 0, 0)
const ONE = new THREE.Vector3(1, 1, 1)

/**
 * Sets the world rotation of a bone
 * @param bone The bone to set the rotation of
 * @param quat The quaternion to set the rotation to
 */
export function lerpBoneWorldRotation (bone: THREE.Bone, quat: THREE.Quaternion, t: number) {
  const target = bone.parent!.getWorldQuaternion(bone.quaternion.clone()).invert().multiply(quat)
  bone.quaternion.slerp(target, t)
  bone.scale.lerp(ONE, t)
}

/**
 * Sets the world rotation of a bone
 * @param bone The bone to set the rotation of
 * @param quat The quaternion to set the rotation to
 */
export function lerpBoneReset (bone: THREE.Bone, t: number) {
  const target = new THREE.Quaternion()
  if (bone.name.indexOf('UpLeg') != -1) {
    target.set(0, 0, -1, 0)
  }

  bone.quaternion.slerp(target, t)
  bone.scale.lerp(ZERO, t)
}
